<template>
  <form class="certificate-form" ref="ndflForm" v-on:submit.prevent="onSubmit">
    <modal-dialog
      v-bind:title="detailsTitle"
      v-model="isShownDetails"
      with-cross
      max-width="700px"
      width="90%"
    >
      <form class="certificate-form__details" v-on:submit.prevent="onSubmitDetails">
        <button
          class="certificate-form__details__invoice"
          type="button"
          v-for="invoice in details"
          v-bind:key="invoice.id"
          v-on:click="onToggleInvoice(invoice.id)"
        >
          <div class="certificate-form__details__invoice__title">{{ invoice.title }}</div>
          <div class="certificate-form__details__positions" v-show="invoice.isOpened">
            <div
              class="certificate-form__details__position"
              v-for="{ position, isSelected } in invoice.positions"
              v-bind:key="position.id"
            >
              <input-checkbox
                v-bind:title="position.name"
                v-bind:value="isSelected"
                v-bind:disabled="onlyView"
                v-on:input="onTogglePosition(position.id)"
              />
              <span>{{ position.amount_title }}</span>
            </div>
          </div>
        </button>
        <button class="certificate-form__details__button-submit" type="submit" v-if="!onlyView">
          Выбрать
        </button>
      </form>
    </modal-dialog>
    <div class="certificate-form__caption">Отчетный год: {{ initialReportingYear }}</div>
    <div class="certificate-form__row">
      <input-letter-text
        mask="############"
        maxlength="12"
        pattern="\d{1,12}"
        title="Номер справки"
        v-bind:disabled="onlyView"
        v-bind:violations="certificateNumberInputErrors"
        v-model="certificateNumber"
      />
      <input-letter-text
        mask="###"
        maxlength="3"
        pattern="\d{1,3}"
        title="Номер корректировки"
        v-bind:disabled="onlyView"
        v-bind:violations="correctionNumberInputErrors"
        v-model="correctionNumber"
      />
    </div>
    <div>
      <span class="certificate-form__subtitle">
        Данные физического лица (его супруга/супруги), оплатившего медицинские услуги (далее –
        налогоплательщик):
      </span>
      <person-section
        v-if="isSinglePerson"
        class="certificate-form__person"
        person-type="patient"
        v-bind:birth-date-violations="patientBirthDateInputErrors"
        v-bind:disabled="onlyView"
        v-bind:identification-code-options="identificationCodeOptions"
        v-bind:identification-code-violations="patientIdentificationCodeInputErrors"
        v-bind:identification-serial-and-number-violations="
          patientIdentificationSerialAndNumberInputErrors
        "
        v-bind:identification-when-issued-violations="patientIdentificationWhenIssuedInputErrors"
        v-bind:initial-birth-date="patientBirthDate"
        v-bind:initial-identification-code="patientIdentificationCode"
        v-bind:initial-identification-serial-and-number="patientIdentificationSerialAndNumber"
        v-bind:initial-identification-when-issued="patientIdentificationWhenIssued"
        v-bind:initial-inn="patientInn"
        v-bind:initial-name="patientName"
        v-bind:initial-only-inn="patientOnlyInn"
        v-bind:initial-patronymic="patientPatronymic"
        v-bind:initial-surname="patientSurname"
        v-bind:inn-violations="patientInnInputErrors"
        v-bind:name-violations="patientNameInputErrors"
        v-bind:patronymic-violations="patientPatronymicInputErrors"
        v-bind:surname-violations="patientSurnameInputErrors"
        v-on:input-birth-date="patientBirthDate = $event"
        v-on:input-identification-code="patientIdentificationCode = $event"
        v-on:input-identification-serial-and-number="patientIdentificationSerialAndNumber = $event"
        v-on:input-identification-when-issued="patientIdentificationWhenIssued = $event"
        v-on:input-inn="patientInn = $event"
        v-on:input-name="patientName = $event"
        v-on:input-only-inn="patientOnlyInn = $event"
        v-on:input-patronymic="patientPatronymic = $event"
        v-on:input-surname="patientSurname = $event"
      />
      <person-section
        v-else
        class="certificate-form__person"
        person-type="guardian"
        v-bind:birth-date-violations="guardianBirthDateInputErrors"
        v-bind:disabled="onlyView"
        v-bind:identification-code-options="identificationCodeOptions"
        v-bind:identification-code-violations="guardianIdentificationCodeInputErrors"
        v-bind:identification-serial-and-number-violations="
          guardianIdentificationSerialAndNumberInputErrors
        "
        v-bind:identification-when-issued-violations="guardianIdentificationWhenIssuedInputErrors"
        v-bind:initial-birth-date="guardianBirthDate"
        v-bind:initial-identification-code="guardianIdentificationCode"
        v-bind:initial-identification-serial-and-number="guardianIdentificationSerialAndNumber"
        v-bind:initial-identification-when-issued="guardianIdentificationWhenIssued"
        v-bind:initial-inn="guardianInn"
        v-bind:initial-name="guardianName"
        v-bind:initial-only-inn="guardianOnlyInn"
        v-bind:initial-patronymic="guardianPatronymic"
        v-bind:initial-surname="guardianSurname"
        v-bind:inn-violations="guardianInnInputErrors"
        v-bind:name-violations="guardianNameInputErrors"
        v-bind:patronymic-violations="guardianPatronymicInputErrors"
        v-bind:surname-violations="guardianSurnameInputErrors"
        v-on:input-birth-date="guardianBirthDate = $event"
        v-on:input-identification-code="guardianIdentificationCode = $event"
        v-on:input-identification-serial-and-number="guardianIdentificationSerialAndNumber = $event"
        v-on:input-identification-when-issued="guardianIdentificationWhenIssued = $event"
        v-on:input-inn="guardianInn = $event"
        v-on:input-name="guardianName = $event"
        v-on:input-only-inn="guardianOnlyInn = $event"
        v-on:input-patronymic="guardianPatronymic = $event"
        v-on:input-surname="guardianSurname = $event"
      />
    </div>
    <input-checkbox
      title="Налогоплательщик и пациент являются одним лицом"
      v-model="isSinglePerson"
      v-bind:disabled="onlyView"
    />
    <div class="certificate-form__row">
      <input-letter-text
        align-right
        maxlength="16"
        title="Сумма расходов на оказанные медицинские услуги по коду услуги «1»"
        v-bind:disabled="onlyView"
        v-bind:mask="rubMask"
        v-bind:violations="amountBySimpleInputErrors"
        v-model="amountBySimple"
      />
      <button
        type="button"
        class="certificate-form__button"
        v-on:click="onShowDetailsByTaxCode($options.taxCodeSimple)"
        title="Показать детали по коду услуги «1»"
      >
        Детали
      </button>
    </div>
    <div class="certificate-form__row">
      <input-letter-text
        align-right
        maxlength="16"
        title="Сумма расходов на оказанные медицинские услуги по коду услуги «2»"
        v-bind:disabled="onlyView"
        v-bind:mask="rubMask"
        v-bind:violations="amountByExpensiveInputErrors"
        v-model="amountByExpensive"
      />
      <button
        class="certificate-form__button"
        type="button"
        title="Показать детали по коду услуги «2»"
        v-on:click="onShowDetailsByTaxCode($options.taxCodeExpensive)"
      >
        Детали
      </button>
    </div>
    <input-letter-text
      mask="##.##.####"
      maxlength="10"
      title="Дата выдачи"
      v-bind:disabled="onlyView"
      v-bind:violations="issuedDateInputErrors"
      v-model="issuedDate"
    />
    <template v-if="!isSinglePerson">
      <div>
        <span class="certificate-form__subtitle">Данные физического лица, которому оказаны медицинские услуги:</span>
        <person-section
          class="certificate-form__person"
          person-type="patient"
          v-bind:birth-date-violations="patientBirthDateInputErrors"
          v-bind:disabled="onlyView"
          v-bind:identification-code-options="identificationCodeOptions"
          v-bind:identification-code-violations="patientIdentificationCodeInputErrors"
          v-bind:identification-serial-and-number-violations="
            patientIdentificationSerialAndNumberInputErrors
          "
          v-bind:identification-when-issued-violations="patientIdentificationWhenIssuedInputErrors"
          v-bind:initial-birth-date="patientBirthDate"
          v-bind:initial-identification-code="patientIdentificationCode"
          v-bind:initial-identification-serial-and-number="patientIdentificationSerialAndNumber"
          v-bind:initial-identification-when-issued="patientIdentificationWhenIssued"
          v-bind:initial-inn="patientInn"
          v-bind:initial-name="patientName"
          v-bind:initial-only-inn="patientOnlyInn"
          v-bind:initial-patronymic="patientPatronymic"
          v-bind:initial-surname="patientSurname"
          v-bind:inn-violations="patientInnInputErrors"
          v-bind:name-violations="patientNameInputErrors"
          v-bind:patronymic-violations="patientPatronymicInputErrors"
          v-bind:surname-violations="patientSurnameInputErrors"
          v-on:input-birth-date="patientBirthDate = $event"
          v-on:input-identification-code="patientIdentificationCode = $event"
          v-on:input-identification-serial-and-number="
            patientIdentificationSerialAndNumber = $event
          "
          v-on:input-identification-when-issued="patientIdentificationWhenIssued = $event"
          v-on:input-inn="patientInn = $event"
          v-on:input-name="patientName = $event"
          v-on:input-only-inn="patientOnlyInn = $event"
          v-on:input-patronymic="patientPatronymic = $event"
          v-on:input-surname="patientSurname = $event"
        />
      </div>
    </template>
    <slot name="actions" />
  </form>
</template>

<script>
import InputCheckbox from '@/components/InputCheckbox.vue';
import InputLetterText from '@/components/InputLetterText.vue';
import InputRadio from '@/components/InputRadio.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import TheSelect from '@/components/TheSelect.vue';
import { FormNdflCertificateRequest } from '@/storage/gateways/RegistryNdflCertificateGateway';
import notifierMixin from '@/mixins/notifierMixin';

/**
 * @description Название события отправки формы.
 * @type {string}
 */
const NAME_EVENT_SUBMIT = 'submit';

/**
 * @description Код услуги «1».
 * @type {number}
 */
const TAX_CODE_SIMPLE = 1;

/**
 * @description Код услуги «2».
 * @type {number}
 */
const TAX_CODE_EXPENSIVE = 2;

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

/**
 * @description Компонент секции физ. лица.
 * @type {object}
 */
const PersonSection = {
  components: { InputLetterText, TheSelect, InputRadio },
  props: {
    birthDateViolations: { default: () => [], type: Array },
    disabled: { default: () => false, type: Boolean },
    identificationCodeOptions: { required: true, type: Array },
    identificationCodeViolations: { default: () => [], type: Array },
    identificationSerialAndNumberViolations: { default: () => [], type: Array },
    identificationWhenIssuedViolations: { default: () => [], type: Array },
    initialBirthDate: { required: true, type: String },
    initialIdentificationCode: { required: true, type: String },
    initialIdentificationSerialAndNumber: { required: true, type: String },
    initialIdentificationWhenIssued: { required: true, type: String },
    initialInn: { required: true, type: String },
    initialName: { required: true, type: String },
    initialPatronymic: { required: true, type: String },
    initialOnlyInn: { required: true, type: Boolean },
    initialSurname: { required: true, type: String },
    innViolations: { default: () => [], type: Array },
    nameViolations: { default: () => [], type: Array },
    patronymicViolations: { default: () => [], type: Array },
    surnameViolations: { default: () => [], type: Array },
    personType: { require: true, type: String },
  },
  render(createElement) {
    const makeField = (
      field,
      value,
      title,
      maxlength,
      violations,
      mask = '',
      optional = false,
    ) => createElement(InputLetterText, {
      attrs: {
        disabled: this.disabled,
      },
      on: {
        input: (updatedValue) => this.$emit(`input-${field}`, updatedValue),
      },
      props: {
        mask,
        maxlength,
        optional,
        title,
        value,
        violations,
      },
    });
    const section = [];
    if (this.initialOnlyInn) {
      section.push(
        makeField('inn', this.initialInn, 'ИНН', 12, this.innViolations, '############', true),
      );
    } else {
      section.push(
        createElement(TheSelect, {
          props: {
            disabled: this.disabled,
            options: this.identificationCodeOptions,
            title: 'Код вида документа',
            value: this.initialIdentificationCode,
            violations: this.identificationCodeViolations,
          },
          on: {
            input: (updatedValue) => this.$emit('input-identification-code', updatedValue),
          },
        }),
      );
      section.push(
        makeField(
          'identification-serial-and-number',
          this.initialIdentificationSerialAndNumber,
          'Серия и номер',
          20,
          this.identificationSerialAndNumberViolations,
        ),
      );
      section.push(
        makeField(
          'identification-when-issued',
          this.initialIdentificationWhenIssued,
          'Дата выдачи',
          10,
          this.identificationWhenIssuedViolations,
          '##.##.####',
        ),
      );
    }
    return createElement('div', [
      makeField('surname', this.initialSurname, 'Фамилия', 36, this.surnameViolations),
      makeField('name', this.initialName, 'Имя', 36, this.nameViolations),
      makeField(
        'patronymic',
        this.initialPatronymic,
        'Отчество',
        36,
        this.patronymicViolations,
        '',
        true,
      ),
      makeField(
        'birth-date',
        this.initialBirthDate,
        'Дата рождения',
        10,
        this.birthDateViolations,
        '##.##.####',
      ),
      createElement(InputRadio, {
        props: {
          identifier: `${this.personType}:only-inn`,
          items: [
            { title: 'ИНН', value: 'inn' },
            { title: 'Документ удостоверяющий личность', value: 'document' },
          ],
          value: this.initialOnlyInn ? 'inn' : 'document',
        },
        on: {
          input: (value) => {
            if (this.disabled) { return; }
            this.$emit('input-only-inn', value === 'inn');
          },
        },
      }),
      ...section,
    ]);
  },
};

/**
 * @description Компонент формы справки НДФЛ.
 */
export default {
  components: {
    InputCheckbox,
    InputLetterText,
    PersonSection,
    ModalDialog,
  },
  computed: {
    amountByExpensiveInputErrors: resolveInputErrors('amount-by-expensive'),
    amountBySimpleInputErrors: resolveInputErrors('amount-by-simple'),
    certificateNumberInputErrors: resolveInputErrors('certificate-number'),
    correctionNumberInputErrors: resolveInputErrors('correction-number'),
    guardianBirthDateInputErrors: resolveInputErrors('guardian-birth-date'),
    guardianIdentificationCodeInputErrors: resolveInputErrors('guardian-identification-code'),
    guardianIdentificationSerialAndNumberInputErrors: resolveInputErrors(
      'guardian-identification-serial-and-number',
    ),
    guardianIdentificationWhenIssuedInputErrors: resolveInputErrors(
      'guardian-identification-when-issued',
    ),
    guardianInnInputErrors: resolveInputErrors('guardian-inn'),
    guardianNameInputErrors: resolveInputErrors('guardian-name'),
    guardianPatronymicInputErrors: resolveInputErrors('guardian-patronymic'),
    guardianSurnameInputErrors: resolveInputErrors('guardian-surname'),
    issuedDateInputErrors: resolveInputErrors('issued-date'),
    patientBirthDateInputErrors: resolveInputErrors('patient-birth-date'),
    patientIdentificationCodeInputErrors: resolveInputErrors('patient-identification-code'),
    patientIdentificationSerialAndNumberInputErrors: resolveInputErrors(
      'patient-identification-serial-and-number',
    ),
    patientIdentificationWhenIssuedInputErrors: resolveInputErrors(
      'patient-identification-when-issued',
    ),
    patientInnInputErrors: resolveInputErrors('patient-inn'),
    patientNameInputErrors: resolveInputErrors('patient-name'),
    patientPatronymicInputErrors: resolveInputErrors('patient-patronymic'),
    patientSurnameInputErrors: resolveInputErrors('patient-surname'),
    reportingYearInputErrors: resolveInputErrors('reporting-year'),
    invoicesByTaxCode() {
      return [TAX_CODE_EXPENSIVE, TAX_CODE_SIMPLE].map((taxCode) => this
        .invoices
        .map((invoice) => {
          const positions = invoice.positions.filter((candidate) => candidate.tax_code === taxCode);
          return ({ ...invoice, positions });
        })
        .filter((invoice) => invoice.positions.length > 0));
    },
    detailsTitle() {
      if (this.selectedTaxCode === null) {
        return '';
      }
      return `Услуги по коду ${this.selectedTaxCode}`;
    },
    details() {
      if (this.selectedTaxCode === null) { return []; }
      const index = this.selectedTaxCode === TAX_CODE_EXPENSIVE ? 0 : 1;
      const invoices = this.invoicesByTaxCode[index];
      return invoices.map((invoice) => ({
        id: invoice.id,
        isOpened: this.openedInvoiceIds.includes(invoice.id),
        title: invoice.title,
        positions: invoice.positions.map((position) => ({
          isSelected: this.selectedPositionIdsInMoment.includes(position.id),
          position,
        })),
      }));
    },
  },
  data() {
    const selectedPositionIds = this
      .invoices
      .flatMap((invoice) => invoice.positions)
      .map(({ id }) => id);
    return {
      amountByExpensive: this.initialAmountByExpensive,
      amountBySimple: this.initialAmountBySimple,
      certificateNumber: this.initialCertificateNumber,
      correctionNumber: this.initialCorrectionNumber,
      guardianBirthDate: this.initialGuardianBirthDate,
      guardianIdentificationCode: this.initialGuardianIdentificationCode,
      guardianIdentificationSerialAndNumber: this.initialGuardianIdentificationSerialAndNumber,
      guardianIdentificationWhenIssued: this.initialGuardianIdentificationWhenIssued,
      guardianInn: this.initialGuardianInn,
      guardianName: this.initialGuardianName,
      guardianOnlyInn: this.initialGuardianOnlyInn,
      guardianPatronymic: this.initialGuardianPatronymic,
      guardianSurname: this.initialGuardianSurname,
      isSinglePerson: this.initialIsSinglePerson,
      issuedDate: this.initialIssuedDate,
      patientBirthDate: this.initialPatientBirthDate,
      patientIdentificationCode: this.initialPatientIdentificationCode,
      patientIdentificationSerialAndNumber: this.initialPatientIdentificationSerialAndNumber,
      patientIdentificationWhenIssued: this.initialPatientIdentificationWhenIssued,
      patientInn: this.initialPatientInn,
      patientName: this.initialPatientName,
      patientOnlyInn: this.initialPatientOnlyInn,
      patientPatronymic: this.initialPatientPatronymic,
      patientSurname: this.initialPatientSurname,
      selectedTaxCode: null,
      openedInvoiceIds: [],
      selectedPositionIds,
      selectedPositionIdsInMoment: [],
      isShownDetails: false,
    };
  },
  methods: {
    /**
     * @description Маска ввода рублей.
     * @param {string} value
     * @returns {any[]}
     */
    rubMask(value) {
      const v = value.replace(/[^0-9/.]{0,16}/gm, '');
      const firstDelimiterIndex = v.indexOf('.');
      const mask = new Array(16).fill(/\d/);
      if (firstDelimiterIndex > 0) {
        mask[firstDelimiterIndex] = '.';
      }
      return mask;
    },
    formRequest() {
      return new FormNdflCertificateRequest(
        this.amountByExpensive,
        this.amountBySimple,
        this.certificateNumber,
        this.correctionNumber,
        this.guardianBirthDate,
        this.guardianIdentificationCode,
        this.guardianIdentificationSerialAndNumber,
        this.guardianIdentificationWhenIssued,
        this.guardianInn,
        this.guardianName,
        this.guardianOnlyInn,
        this.guardianPatronymic,
        this.guardianSurname,
        this.isSinglePerson,
        this.issuedDate,
        this.patientBirthDate,
        this.patientIdentificationCode,
        this.patientIdentificationSerialAndNumber,
        this.patientIdentificationWhenIssued,
        this.patientInn,
        this.patientName,
        this.patientOnlyInn,
        this.patientPatronymic,
        this.patientSurname,
        this.selectedPositionIds,
        this.initialReportingYear,
        this.initialReportingType,
      );
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      const isNativeValidationSuccess = this.$refs.ndflForm.reportValidity();
      if (isNativeValidationSuccess !== true) {
        this.notifyFail('Не удалось отправить данные, проверьте правильность заполнения');
        return;
      }
      const request = this.formRequest();
      this.$emit(NAME_EVENT_SUBMIT, request);
    },
    /**
     * @description Переключить видимость позиций счета.
     * @param {number} invoiceId
     * @returns {void}
     */
    onToggleInvoice(invoiceId) {
      const index = this.openedInvoiceIds.indexOf(invoiceId);
      if (index === -1) {
        this.openedInvoiceIds.push(invoiceId);
      } else {
        this.openedInvoiceIds.splice(index, 1);
      }
    },
    /**
     * @description Переключить выбор позиции счета.
     * @param {number} positionId
     * @returns {void}
     */
    onTogglePosition(positionId) {
      const index = this.selectedPositionIdsInMoment.indexOf(positionId);
      if (index === -1) {
        this.selectedPositionIdsInMoment.push(positionId);
      } else {
        this.selectedPositionIdsInMoment.splice(index, 1);
      }
    },
    onShowDetailsByTaxCode(taxCode) {
      this.selectedPositionIdsInMoment = [...this.selectedPositionIds];
      this.selectedTaxCode = taxCode;
      this.isShownDetails = true;
    },
    onSubmitDetails() {
      const totalInKop = this.invoices
        .flatMap((invoice) => invoice.positions)
        .filter(
          (position) => position.tax_code === this.selectedTaxCode
            && this.selectedPositionIdsInMoment.includes(position.id),
        )
        .reduce((total, position) => total + position.amount_value, 0);
      const totalInRub = (totalInKop / 100).toFixed(2);
      if (this.selectedTaxCode === TAX_CODE_SIMPLE) {
        this.amountBySimple = totalInRub;
      } else {
        this.amountByExpensive = totalInRub;
      }
      this.selectedPositionIds = [...this.selectedPositionIdsInMoment];
      this.isShownDetails = false;
    },
  },
  name: 'NdflCertificateForm',
  props: {
    identificationCodeOptions: { default: () => [], type: Array },
    initialAmountByExpensive: { default: () => '', type: String },
    initialAmountBySimple: { default: () => '', type: String },
    initialCertificateNumber: { default: () => '', type: String },
    initialCorrectionNumber: { default: () => '', type: String },
    initialGuardianBirthDate: { default: () => '', type: String },
    initialGuardianIdentificationCode: { default: () => '', type: String },
    initialGuardianIdentificationSerialAndNumber: { default: () => '', type: String },
    initialGuardianIdentificationWhenIssued: { default: () => '', type: String },
    initialGuardianInn: { default: () => '', type: String },
    initialGuardianName: { default: () => '', type: String },
    initialGuardianOnlyInn: { default: () => true, type: Boolean },
    initialGuardianPatronymic: { default: () => '', type: String },
    initialGuardianSurname: { default: () => '', type: String },
    initialIsSinglePerson: { default: () => false, type: Boolean },
    initialIssuedDate: { default: () => '', type: String },
    initialPatientBirthDate: { default: () => '', type: String },
    initialPatientIdentificationCode: { default: () => '', type: String },
    initialPatientIdentificationSerialAndNumber: { default: () => '', type: String },
    initialPatientIdentificationWhenIssued: { default: () => '', type: String },
    initialPatientInn: { default: () => '', type: String },
    initialPatientName: { default: () => '', type: String },
    initialPatientOnlyInn: { default: () => true, type: Boolean },
    initialPatientPatronymic: { default: () => '', type: String },
    initialPatientSurname: { default: () => '', type: String },
    initialReportingYear: { default: () => '', type: [String, Number] },
    initialReportingType: { default: () => '', type: String },
    inputErrors: { required: true, type: Object },
    invoices: { required: true, type: Array },
    onlyView: { default: () => false, type: Boolean },
  },
  mixins: [notifierMixin],
  taxCodeSimple: TAX_CODE_SIMPLE,
  taxCodeExpensive: TAX_CODE_EXPENSIVE,
};
</script>

<style scss lang="scss">
@use "@/scss/buttons";

.certificate-form {
  display: flex;
  flex-direction: column;
  gap: 25px;
  background: #F2F5F8;
  padding: 42px 42px 50px 46px;
  font-family: "Lato", sans-serif;
  &__details {
    display: flex;
    flex-direction: column;
    margin: 10px 0 0 0;
    gap: 15px;

    &__button-submit {
      max-width: 200px;
      @include buttons.btn-blue(100%, 42px, 16px);
      margin: 20px 0 0 0;
      align-self: center;
    }

    &__invoice {
      display: flex;
      flex-direction: column;
      gap: 10px;
      text-align: left;
      &__title {
        &:hover {
          text-decoration: underline;
        }

      }
    }

    &__positions {
      display: flex;
      flex-direction: column;
      gap: 5px;
      width: 100%;
    }

    &__position {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      justify-content: space-between;
      gap: 10px;
      width: 100%;
      .checkbox>.checkbox__title {
        align-items: flex-start;
      }
      span {
        margin: 0 0 0 auto;
        font-size: 16px;
        line-height: 19px;

      }
    }
  }

  &__row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px 30px;
  }
  &__caption {
    font-size: 24px;
    line-height: 28px;
    font-weight: 600;

  }
  &__subtitle {
    display: flex;
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 10px 0;
  }
  &__person {
    display: flex;
    flex-direction: column;
    gap: 10px;
    position: relative;
  }
  &__button {
    @include buttons.btn-blue(150px, 42px, 16px);
  }
}
@media screen and (max-width: 480px) {
  .certificate-form {
    padding: 10px
  }
}
</style>
